// Fichier: src/pages/accounting/AccountLedgerDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, IconButton, Chip, TextField, MenuItem, CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, doc, getDoc, onSnapshot, query, where, orderBy } from 'firebase/firestore';

const AccountLedgerDetail = () => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const yearFilter = searchParams.get('year') || 'all';

  useEffect(() => {
    const loadAccount = async () => {
      const snap = await getDoc(doc(db, 'accounting_accounts', accountId));
      if (snap.exists()) setAccount({ id: snap.id, ...snap.data() });
    };
    loadAccount();
  }, [accountId]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'accounting_entries'), where('status', '==', 'validé'), orderBy('date', 'asc')),
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const years = useMemo(() => {
    const set = new Set(entries.map((e) => e.exerciceId).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [entries]);

  const movements = useMemo(() => {
    let running = 0;
    const rows = [];
    entries
      .filter((e) => yearFilter === 'all' || e.exerciceId === yearFilter)
      .forEach((e) => {
        (e.lines || []).forEach((l) => {
          if (l.accountId !== accountId) return;
          const debit = Number(l.debit) || 0;
          const credit = Number(l.credit) || 0;
          const delta = account?.normalSide === 'credit' ? credit - debit : debit - credit;
          running += delta;
          rows.push({
            key: `${e.id}-${l.accountId}-${rows.length}`,
            date: e.date,
            reference: e.reference,
            description: e.description,
            lineLabel: l.lineLabel,
            debit,
            credit,
            running
          });
        });
      });
    return rows;
  }, [entries, accountId, yearFilter, account]);

  const formatDate = (d) => {
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('fr-BE');
  };

  const totalDebit = movements.reduce((s, m) => s + m.debit, 0);
  const totalCredit = movements.reduce((s, m) => s + m.credit, 0);
  const finalBalance = movements.length > 0 ? movements[movements.length - 1].running : 0;

  if (loading || !account) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting/ledger')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {account.code} — {account.label}
            </Typography>
            <Chip
              size="small"
              label={`Sens normal : ${account.normalSide === 'credit' ? 'Crédit' : 'Débit'}`}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
        <TextField
          select
          size="small"
          label="Exercice"
          value={yearFilter}
          onChange={(e) => setSearchParams(e.target.value === 'all' ? {} : { year: e.target.value })}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">Toutes années</MenuItem>
          {years.map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Référence</strong></TableCell>
              <TableCell><strong>Libellé</strong></TableCell>
              <TableCell align="right"><strong>Débit</strong></TableCell>
              <TableCell align="right"><strong>Crédit</strong></TableCell>
              <TableCell align="right"><strong>Solde cumulé</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun mouvement validé sur ce compte pour cette période.
                </TableCell>
              </TableRow>
            )}
            {movements.map((m) => (
              <TableRow key={m.key} hover>
                <TableCell>{formatDate(m.date)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{m.reference}</TableCell>
                <TableCell>{m.lineLabel || m.description}</TableCell>
                <TableCell align="right">{m.debit > 0 ? `${m.debit.toFixed(2)} €` : ''}</TableCell>
                <TableCell align="right">{m.credit > 0 ? `${m.credit.toFixed(2)} €` : ''}</TableCell>
                <TableCell align="right">{m.running.toFixed(2)} €</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
        <Typography>Total débit : <strong>{totalDebit.toFixed(2)} €</strong></Typography>
        <Typography>Total crédit : <strong>{totalCredit.toFixed(2)} €</strong></Typography>
        <Typography>Solde final : <strong>{finalBalance.toFixed(2)} €</strong></Typography>
      </Paper>
    </Container>
  );
};

export default AccountLedgerDetail;
